import { cva } from "class-variance-authority";
import React, { PropsWithChildren, ReactNode } from "react";

//icons
import { faInfoCircle } from "@performyard/fontawesome/pro-regular-svg-icons";

//context
import { InputFieldProvider, useInputField } from "./InputContext";

import { cn } from "../../../lib/utils";

//atoms
import { Icon } from "../../atoms/Icon";
import { Text, TextProps } from "../../atoms/Text";
import { AnimatePresence, SubtleTopSlideIn } from "../../organisms/AnimatePresence";
import { Tooltip } from "../Tooltip";

export type InputWrapperProps = {
	/**
	 * The id of the input field, will get applied to all inner inputs unless overridden with another input wrapper OR one is directly set on the input
	 */
	id: string;
	/**
	 * sets error state on all inner inputs that use the useInputField context hook and renders an error message under the input
	 */
	error?: string;
	/**
	 * disables all inner inputs that use the useInputField context hook
	 */
	disabled?: boolean;
	/**
	 * The label of the input field
	 */
	label?: string | ReactNode;
	/**
	 * The position of the label, either top, left or right
	 * @default top
	 */
	labelPosition?: "top" | "left" | "right";
	/**
	 * The justify-content value for the flex container when labelPosition is left or right
	 * @default "start"
	 */
	justify?: "start" | "end" | "center" | "between" | "around" | "evenly";
	/**
	 * The align-items value for the flex container when labelPosition is left or right
	 * @default "center"
	 */
	align?: "start" | "end" | "center" | "baseline" | "stretch";
	/**
	 * The info message of the input field, renders as a tooltip on the right of the label
	 */
	info?: string;
	/**
	 * Whether the wrapper is the group legend
	 * @default false
	 */
	group?: boolean;
	/**
	 * Whether to disable label click behavior (htmlFor attribute)
	 * @default false
	 */
	disableLabelClick?: boolean;

	dangerouslySetClassName?: string;

	"data-testid"?: string;
};

const inputWrapperVariants = cva("flex gap-2", {
	variants: {
		labelPosition: {
			top: "flex-col",
			left: "flex-row",
			right: "flex-row",
		},
		justify: {
			start: "justify-start",
			end: "justify-end",
			center: "justify-center",
			between: "justify-between",
			around: "justify-around",
			evenly: "justify-evenly",
		},
		align: {
			start: "items-start",
			end: "items-end",
			center: "items-center",
			baseline: "items-baseline",
			stretch: "items-stretch",
		},
	},
	defaultVariants: {
		labelPosition: "top",
	},
});

/**
 * InputWrapper is a component that wraps an input field and provides a label, error message, and info message.
 * It also provides a context for the input field to use.
 * It can be recursively used to wrap nested inputs. so that shared errored or disabled states can be applied to all children inputs.
 */
export const InputWrapper = ({
	id,
	children,
	label,
	labelPosition = "top",
	justify,
	align,
	dangerouslySetClassName,
	group = false,
	disableLabelClick = false,
	...props
}: PropsWithChildren<InputWrapperProps>) => {
	const { error, disabled = props.disabled, info = props.info } = useInputField();

	return (
		<InputFieldProvider
			id={id}
			error={props.error || error}
			disabled={disabled}
			info={info}
			disableLabelClick={disableLabelClick}
		>
			<div
				className={cn("flex w-full flex-col gap-2", dangerouslySetClassName)}
				role={group ? "group" : undefined}
				aria-labelledby={group ? `label-${id}` : undefined}
			>
				<div className={inputWrapperVariants({ labelPosition, justify, align })}>
					{labelPosition === "right" && children}
					{label && <InputLabel>{label}</InputLabel>}
					{labelPosition !== "right" && children}
				</div>
				<AnimatePresence>
					{props?.error && (
						<SubtleTopSlideIn>
							<InputError />
						</SubtleTopSlideIn>
					)}
				</AnimatePresence>
			</div>
		</InputFieldProvider>
	);
};

/**
 * A label that is displayed alongside the input
 */
const InputLabel = React.forwardRef<HTMLLabelElement, Partial<Omit<TextProps, "htmlFor">>>((props, ref) => {
	const { error, id, disableLabelClick } = useInputField();

	const defaultProps: TextProps = {
		size: "paragraph-3",
		color: "neutral-9",
		weight: "semi-bold",
		overrideElement: "label",
		children: "",
	};

	const overrideProps: Partial<TextProps> = {
		color: error ? "red-6" : props?.color,
		htmlFor: disableLabelClick ? undefined : id,
	};

	return (
		<div className="flex items-center">
			<Text id={"label-" + id} ref={ref} {...defaultProps} {...props} {...overrideProps} />
			<InputInfo />
		</div>
	);
});
InputLabel.displayName = "InputLabel";

/**
 * A legend that is displayed alongside the input
 */
const InputLegend = React.forwardRef<HTMLLegendElement, Partial<Omit<TextProps, "htmlFor">>>((props, ref) => {
	const { error, id } = useInputField();

	const defaultProps: TextProps = {
		size: "paragraph-3",
		color: "neutral-9",
		weight: "semi-bold",
		overrideElement: "legend",
		children: "",
	};

	const overrideProps: Partial<TextProps> = {
		color: error ? "red-5" : props?.color,
	};

	return (
		<div className="flex items-center">
			<Text id={"legend-" + id} ref={ref} htmlFor={id} {...defaultProps} {...props} {...overrideProps} />
			<InputInfo />
		</div>
	);
});
InputLegend.displayName = "InputLegend";

/**
 * An info icon that is displayed on the right of the label
 */
const InputInfo = () => {
	const { info, error } = useInputField();

	if (!info) return null;

	return (
		<Tooltip content={info}>
			<Icon icon={faInfoCircle} size={16} color={error ? "red-5" : "neutral-7"} aria-label="More information" />
		</Tooltip>
	);
};

/**
 * An error message that is displayed below the input
 */
const InputError = () => {
	const { error, id } = useInputField();

	return (
		<div className="flex items-center gap-1" role="alert" aria-live="assertive">
			<Icon icon={faInfoCircle} size={16} color="red-5" aria-hidden="true" />
			<Text size="caption" color="red-5" aria-live="polite" id={`error-${id}`}>
				{error || ""}
			</Text>
		</div>
	);
};
